/**
 * O que cada um está ouvindo: o `music_status.dart` e o `top_music_track.dart`.
 *
 * Os campos ficam com o nome que o Last.fm e o armazém já usam (`trackName`,
 * `isPlaying`, `listenUrl`), e não em português: o documento gravado é o mesmo
 * que o Flutter grava, e traduzir as chaves aqui deixaria as duas metades da
 * casa lendo gavetas diferentes da mesma coleção.
 */

import { enderecoDeFora } from '../nucleo/endereco';
import { COLECOES } from '../types/colecoes';

export const COLECAO_MUSICA = COLECOES.musica;

export interface EstadoDeMusica {
  username: string;
  trackName: string;
  artistName: string;
  albumName: string;
  imageUrl?: string | null;
  isPlaying: boolean;
  /** Onde a música toca: uma busca no YouTube Music por artista + título. */
  listenUrl: string;
  /** Milissegundos, ou `null` quando o Last.fm não datou o scrobble. */
  timestamp?: number | null;
}

/** Uma faixa do `user.gettoptracks`: tem posição e contagem, não tem álbum. */
export interface FaixaMaisTocada {
  rank: number;
  trackName: string;
  artistName: string;
  playCount: number;
  imageUrl?: string | null;
  listenUrl: string;
  mbid?: string | null;
}

/**
 * O Last.fm serve algumas imagens de "sem capa" no lugar da capa de verdade.
 * São dois hashes conhecidos: um quadrado claro com uma estrela branca e um
 * disco cinza. Mostrá-los enche a lista de miniaturas idênticas com cara de
 * quebrado, então quem chama trata como "não tem imagem".
 */
const CAPAS_VAZIAS = ['2a96cbd8b46e442fc41c2b86b821562f', 'c6f59c1e5e7240a4c0d427abd71f3dbb'];

export const capaVaziaDoLastfm = (url: string): boolean =>
  CAPAS_VAZIAS.some((h) => url.includes(h));

/** `null` quando o endereço é vazio ou é um dos dois marcadores conhecidos. */
export function limparCapaDoLastfm(url?: string | null): string | null {
  if (!url) return null;
  return capaVaziaDoLastfm(url) ? null : url;
}

/**
 * Onde uma música do Last.fm vai parar quando alguém toca nela — o
 * `youtubeMusicSearchUrl` de `lib/core/music/youtube_music.dart`.
 *
 * O Last.fm entrega nome e artista, não um id de catálogo, então o link é
 * sempre uma busca. A conta não entra no link: o YouTube Music apaga o
 * `authuser` da URL antes de carregar a página. Quem escolhe a conta é o botão
 * dos ajustes (`enderecoDaContaDeMusica`), uma vez por navegador.
 *
 * `URLSearchParams` e não `encodeURIComponent` porque o `Uri.encodeQueryComponent`
 * do Dart escreve o espaço como `+`, e é esse o link que já foi gravado nos
 * documentos antigos.
 */
export function buscaNoYoutubeMusic(artista: string, faixa: string): string {
  const termo = [artista, faixa].filter((p) => p.trim() !== '').join(' ');
  return `https://music.youtube.com/search?${new URLSearchParams({ q: termo })}`;
}

/** A capa `extralarge` da lista de imagens do Last.fm, ou a última que vier. */
function capaDaLista(imagens: unknown): string | null {
  if (!Array.isArray(imagens) || imagens.length === 0) return null;
  let escolhida = imagens[imagens.length - 1] as Record<string, unknown>;
  for (const img of imagens) {
    if (img && typeof img === 'object' && (img as Record<string, unknown>).size === 'extralarge') {
      escolhida = img as Record<string, unknown>;
      break;
    }
  }
  const texto = escolhida?.['#text'];
  return limparCapaDoLastfm(typeof texto === 'string' ? texto : null);
}

/** `MusicStatus.fromTrackJson`: uma entrada do `user.getrecenttracks`. */
export function estadoDeFaixa(faixa: Record<string, unknown>, usuario: string): EstadoDeMusica {
  const nome = `${faixa.name ?? ''}`;
  const artista = `${(faixa.artist as Record<string, unknown> | undefined)?.['#text'] ?? ''}`;
  const album = `${(faixa.album as Record<string, unknown> | undefined)?.['#text'] ?? ''}`;

  const attr = faixa['@attr'] as Record<string, unknown> | undefined;
  const data = faixa.date as Record<string, unknown> | undefined;
  const uts = data ? Number.parseInt(`${data.uts}`, 10) : Number.NaN;

  return {
    username: usuario,
    trackName: nome,
    artistName: artista,
    albumName: album,
    imageUrl: capaDaLista(faixa.image),
    isPlaying: attr?.nowplaying === 'true',
    listenUrl: buscaNoYoutubeMusic(artista, nome),
    timestamp: Number.isFinite(uts) ? uts * 1000 : null,
  };
}

/** `TopMusicTrack.fromJson`: uma entrada do `user.gettoptracks`. */
export function faixaMaisTocadaDeJson(faixa: Record<string, unknown>): FaixaMaisTocada {
  const artistaBruto = faixa.artist;
  const artista =
    artistaBruto && typeof artistaBruto === 'object'
      ? `${(artistaBruto as Record<string, unknown>).name ?? 'Unknown Artist'}`
      : 'Unknown Artist';
  const nome = typeof faixa.name === 'string' ? faixa.name : 'Unknown Track';

  const attr = faixa['@attr'] as Record<string, unknown> | undefined;
  const rank = Number.parseInt(`${attr?.rank ?? ''}`, 10);
  const tocadas = Number.parseInt(`${faixa.playcount ?? ''}`, 10);
  const mbid = typeof faixa.mbid === 'string' && faixa.mbid !== '' ? faixa.mbid : null;

  return {
    rank: Number.isFinite(rank) ? rank : 0,
    trackName: nome,
    artistName: artista,
    playCount: Number.isFinite(tocadas) ? tocadas : 0,
    imageUrl: capaDaLista(faixa.image),
    listenUrl: buscaNoYoutubeMusic(artista, nome),
    mbid,
  };
}

/** `MusicStatus.fromMap`: o que estava gravado no armazém. */
export function estadoDeMapa(mapa: Record<string, unknown>): EstadoDeMusica {
  // O Flutter grava o `timestamp` como ISO 8601; aqui ele anda em milissegundos
  // para não haver duas representações do mesmo instante circulando na tela.
  const bruto = mapa.timestamp;
  const quando = typeof bruto === 'string' ? Date.parse(bruto) : Number(bruto);

  const artista = `${mapa.artistName ?? 'Unknown Artist'}`;
  const nome = `${mapa.trackName ?? 'Silent Night'}`;

  return {
    username: `${mapa.username ?? ''}`,
    trackName: nome,
    artistName: artista,
    albumName: `${mapa.albumName ?? 'No Album'}`,
    imageUrl: typeof mapa.imageUrl === 'string' ? mapa.imageUrl : null,
    isPlaying: mapa.isPlaying === true,
    /* O documento é escrito por quem ouve, e o campo vira `href` no site e
       `Linking.openURL` no celular. O Flutter devolve o texto cru; aqui ele
       passa pela entrada, e o que não for endereço de navegador cai na mesma
       busca que os outros caminhos deste módulo já montam. */
    listenUrl: enderecoDeFora(`${mapa.listenUrl ?? ''}`, buscaNoYoutubeMusic(artista, nome)),
    timestamp: Number.isFinite(quando) ? quando : null,
  };
}

/** `MusicStatus.toMap`: de volta ao formato que o Flutter também lê. */
export function estadoParaMapa(estado: EstadoDeMusica): Record<string, unknown> {
  return {
    username: estado.username,
    trackName: estado.trackName,
    artistName: estado.artistName,
    albumName: estado.albumName,
    imageUrl: estado.imageUrl ?? null,
    isPlaying: estado.isPlaying,
    listenUrl: estado.listenUrl,
    timestamp: estado.timestamp ? new Date(estado.timestamp).toISOString() : null,
  };
}

/** `MusicStatus.empty`: quem vinculou a conta mas ainda não ouviu nada. */
export function estadoVazio(usuario: string): EstadoDeMusica {
  return {
    username: usuario,
    trackName: 'Silent Night',
    artistName: 'Unknown Artist',
    albumName: 'No Album',
    imageUrl: null,
    isPlaying: false,
    listenUrl: buscaNoYoutubeMusic('Unknown Artist', ''),
    timestamp: null,
  };
}
